import React from 'react'
import styles from './MicrositeTeamSectionForm.module.css'
import TextInput from '../../../components/TextInput/TextInput'
import MicrositeImageUploader from '../../../components/MicrositeImageUploader/MicrositeImageUploader'
import MicrositeRepeatableRows from '../../../components/MicrositeRepeatableRows/MicrositeRepeatableRows'

// ──────────────────────────────────────────────────────────────
// Default skeleton matching the real production content shape.
// ──────────────────────────────────────────────────────────────
const DEFAULT_CONTENT = {
    title: '',
    highlightedTitle: '',
    subtitle: '',
    description: '',
    members: []
}

const EMPTY_MEMBER = {
    name: '',
    role: '',
    image: '',
    socialLinks: { instagram: '', facebook: '', twitter: '' }
}

// Backfills any missing key, including each member's nested
// socialLinks object, so older or partially-saved content still
// renders without crashing.
const withDefaults = (content) => ({
    title: content?.title ?? DEFAULT_CONTENT.title,
    highlightedTitle: content?.highlightedTitle ?? DEFAULT_CONTENT.highlightedTitle,
    subtitle: content?.subtitle ?? DEFAULT_CONTENT.subtitle,
    description: content?.description ?? DEFAULT_CONTENT.description,
    members: (content?.members || []).map((m) => ({
        ...EMPTY_MEMBER,
        ...m,
        socialLinks: { ...EMPTY_MEMBER.socialLinks, ...(m?.socialLinks || {}) }
    }))
})

/**
 * MicrositeTeamSectionForm
 * Content editor for the landing page "Our Team" section, matching
 * the real production content shape: title (with an optional
 * highlighted word/phrase), subtitle, description, plus a
 * repeatable list of team members. Each member has a name, role,
 * a photo (real field name is `image`, not `photo`), and a
 * socialLinks object (instagram/facebook/twitter) — there's no
 * bio field in the real shape, so it was dropped here.
 *
 * Pure presentational sub-form — receives `content` and `onChange`
 * from MicrositeLandingSection, which owns loading/saving/versioning.
 * No DB or API changes required: osm_store_config_landing.content is
 * a generic JSON column already; this form just sends the matching
 * object into the same micrositeUpdateLandingSection contract.
 */
const MicrositeTeamSectionForm = ({ content, onChange }) => {
    const safe = withDefaults(content)

    const update = (field, value) => onChange?.({ ...safe, [field]: value })

    return (
        <section className={styles.fieldGroup}>
            <h2 className={styles.groupTitle}>Our team</h2>
            <p className={styles.groupDesc}>Introduce the people behind your salon.</p>

            <TextInput
                label="Title"
                value={safe.title}
                onChange={(v) => update('title', v)}
                placeholder="e.g. Meet Our"
                maxLength={80}
                showCharCount
            />
            <TextInput
                label="Highlighted word/phrase (optional)"
                value={safe.highlightedTitle}
                onChange={(v) => update('highlightedTitle', v)}
                placeholder="e.g. Dream Team"
                helperText="Shown in an accent color, right after the title"
            />
            <TextInput
                label="Subtitle"
                value={safe.subtitle}
                onChange={(v) => update('subtitle', v)}
                placeholder="e.g. Our Experts"
                maxLength={60}
                showCharCount
            />
            <TextInput
                label="Description"
                value={safe.description}
                onChange={(v) => update('description', v)}
                placeholder="e.g. Talent doesn't just happen, it's cultivated."
                maxLength={220}
                showCharCount
            />

            <label className={styles.rowsLabel}>Team members</label>
            <MicrositeRepeatableRows
                items={safe.members}
                onChange={(v) => update('members', v)}
                emptyItem={{ ...EMPTY_MEMBER, socialLinks: { ...EMPTY_MEMBER.socialLinks } }}
                addLabel="Add team member"
                renderSummary={(item) => item.name || 'New team member'}
                renderItem={(item, idx, updateItem) => {
                    const updateSocial = (platform, value) =>
                        updateItem('socialLinks', { ...item.socialLinks, [platform]: value })

                    return (
                        <div className={styles.memberFields}>
                            <div className={styles.row2}>
                                <TextInput
                                    label="Name"
                                    value={item.name}
                                    onChange={(v) => updateItem('name', v)}
                                    placeholder="e.g. Sarah Jones"
                                />
                                <TextInput
                                    label="Role"
                                    value={item.role}
                                    onChange={(v) => updateItem('role', v)}
                                    placeholder="e.g. Senior Stylist"
                                />
                            </div>

                            <MicrositeImageUploader
                                label="Photo"
                                value={item.image}
                                onChange={(v) => updateItem('image', v)}
                                placeholder="https://example.com/photo.jpg"
                            />

                            <label className={styles.socialLabel}>Social links</label>
                            <div className={styles.row3}>
                                <TextInput
                                    label="Instagram"
                                    value={item.socialLinks.instagram}
                                    onChange={(v) => updateSocial('instagram', v)}
                                    placeholder="https://instagram.com/..."
                                />
                                <TextInput
                                    label="Facebook"
                                    value={item.socialLinks.facebook}
                                    onChange={(v) => updateSocial('facebook', v)}
                                    placeholder="https://facebook.com/..."
                                />
                                <TextInput
                                    label="Twitter"
                                    value={item.socialLinks.twitter}
                                    onChange={(v) => updateSocial('twitter', v)}
                                    placeholder="https://twitter.com/..."
                                />
                            </div>
                        </div>
                    )
                }}
            />
        </section>
    )
}

export default MicrositeTeamSectionForm